import { unwrap } from "@sagot/api-client";
import { TIN_PATTERN } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { Button, Checkbox, Dialog, DialogContent, Field, Input, Select, toast } from "@sagot/ui";
import { useMutation } from "@tanstack/react-query";
import { useForm, useWatch } from "react-hook-form";
import { z } from "zod";
import { useErrorMessage } from "../../../lib/api-errors";
import { zodResolver } from "../../../lib/zod-resolver";
import { useApi } from "../../../session/session-provider";
import type { BillingDetailsOut } from "../shared";
import { useInvalidateBilling } from "./use-billing";

const TAX_TYPES = ["vat_registered", "non_vat", "vat_exempt"] as const;

const schema = z.object({
  registeredName: z.string().trim().min(1, "billing.details.errors.name").max(200),
  tin: z.string().trim().regex(TIN_PATTERN, "billing.details.errors.tin"),
  receiptEmail: z.union([z.literal(""), z.string().trim().email("billing.details.errors.email").max(254)]),
  line1: z.string().trim().min(1, "billing.details.errors.line1").max(200),
  line2: z.string().trim().max(200),
  city: z.string().trim().min(1, "billing.details.errors.city").max(100),
  province: z.string().trim().max(200),
  zip: z.string().trim().max(10),
  taxType: z.union([z.literal(""), z.enum(TAX_TYPES)]),
  vatExempt: z.boolean(),
  exemptionRef: z.string().trim().max(100),
});
type FormValues = z.infer<typeof schema>;

function text(value: unknown): string {
  return typeof value === "string" ? value : "";
}

function defaults(details: BillingDetailsOut): FormValues {
  const address = details.address;
  const taxType = TAX_TYPES.find((type) => type === details.tax_type) ?? "";
  return {
    registeredName: details.registered_name ?? "",
    tin: details.tin ?? "",
    receiptEmail: details.receipt_email ?? "",
    line1: text(address.line1),
    line2: text(address.line2),
    city: text(address.city),
    province: text(address.province),
    zip: text(address.zip),
    taxType,
    vatExempt: details.vat_exempt,
    exemptionRef: details.vat_exemption_ref ?? "",
  };
}

const orNull = (value: string) => (value ? value : null);

/** BIR billing profile printed on official receipts (`PUT /v1/billing/details`). */
export function BillingDetailsDialog({ details, onClose }: { details: BillingDetailsOut; onClose: () => void }) {
  const { t } = useTranslation();
  const api = useApi();
  const message = useErrorMessage();
  const invalidate = useInvalidateBilling();
  const form = useForm<FormValues>({ resolver: zodResolver(schema), defaultValues: defaults(details) });
  const save = useMutation({
    mutationFn: (v: FormValues) =>
      unwrap(
        api.PUT("/v1/billing/details", {
          body: {
            registered_name: v.registeredName,
            tin: v.tin,
            receipt_email: orNull(v.receiptEmail),
            address: { line1: v.line1, line2: orNull(v.line2), city: v.city, province: orNull(v.province), zip: orNull(v.zip) },
            tax_type: v.taxType || null,
            vat_exempt: v.vatExempt,
            vat_exemption_ref: v.vatExempt ? orNull(v.exemptionRef) : null,
          },
        }),
      ),
    onSuccess: async () => {
      toast.success(t("billing.details.saved"));
      await invalidate();
      onClose();
    },
    onError: (caught) => toast.error(message(caught)),
  });
  const errors = form.formState.errors;
  const err = (key: keyof FormValues) => (errors[key]?.message ? t(errors[key].message) : undefined);
  const submit = form.handleSubmit((values) => { save.mutate(values); });
  const vatExempt = useWatch({ control: form.control, name: "vatExempt" });
  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent
        title={t("billing.details.title")}
        closeLabel={t("common.close")}
        footer={
          <>
            <Button variant="secondary" onClick={onClose}>
              {t("common.cancel")}
            </Button>
            <Button type="submit" form="details-form" loading={save.isPending}>
              {t("common.save")}
            </Button>
          </>
        }
      >
        <form id="details-form" className="space-y-4" onSubmit={(event) => void submit(event)} noValidate>
          <Field label={t("billing.details.registeredName")} error={err("registeredName")}>
            <Input autoComplete="organization" {...form.register("registeredName")} />
          </Field>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field label={t("billing.details.tin")} error={err("tin")} hint={t("billing.details.tinHint")}>
              <Input className="font-mono" placeholder="000-000-000-000" {...form.register("tin")} />
            </Field>
            <Field label={t("billing.details.receiptEmail")} error={err("receiptEmail")}>
              <Input type="email" autoComplete="email" {...form.register("receiptEmail")} />
            </Field>
          </div>
          <Field label={t("billing.details.line1")} error={err("line1")} hint={t("billing.details.addressHint")}>
            <Input autoComplete="address-line1" {...form.register("line1")} />
          </Field>
          <Field label={t("billing.details.line2")}>
            <Input autoComplete="address-line2" {...form.register("line2")} />
          </Field>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <Field label={t("billing.details.city")} error={err("city")}>
              <Input autoComplete="address-level2" {...form.register("city")} />
            </Field>
            <Field label={t("billing.details.province")}>
              <Input autoComplete="address-level1" {...form.register("province")} />
            </Field>
            <Field label={t("billing.details.zip")}>
              <Input autoComplete="postal-code" inputMode="numeric" {...form.register("zip")} />
            </Field>
          </div>
          <Field label={t("billing.details.taxType")}>
            <Select {...form.register("taxType")}>
              <option value="">{t("billing.details.taxUnset")}</option>
              {TAX_TYPES.map((type) => (
                <option key={type} value={type}>
                  {t(`billing.details.tax_${type}`)}
                </option>
              ))}
            </Select>
          </Field>
          <Checkbox label={t("billing.details.vatExempt")} description={t("billing.details.vatExemptHint")} checked={vatExempt} onCheckedChange={(checked) => { form.setValue("vatExempt", checked === true); }} />
          {vatExempt ? (
            <Field label={t("billing.details.exemptionRef")}>
              <Input {...form.register("exemptionRef")} />
            </Field>
          ) : null}
        </form>
      </DialogContent>
    </Dialog>
  );
}
